import React, { FC, ReactNode } from "react";

import {
  Avatar,
  Divider,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { Carousel, CarouselSlide } from "../../components/Carousel";
import { Logo } from "../../components/Logo";
import { useSettings } from "../../hooks/use-settings";
import EasilyManage from "../../icons/signup/EasilyManage";
import IntuitiveApi from "../../icons/signup/IntuitiveApi";
import PrivateData from "../../icons/signup/PrivateData";
import Users from "../../icons/signup/Users";
import { purple } from "../../theme/colors";

type ShellProps = {
  children: ReactNode;
};

const LoginShell: FC<ShellProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const settings = useSettings();
  const logo = (
    <Logo
      height={40}
      color={settings.paletteMode === "light" ? purple.main : "white"}
    />
  );

  if (isMobile) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Stack spacing={5}>
          <Box
            component={Link}
            href={
              "https://www.pomerium.com/?utm_source=zero&utm_medium=landing&utm_campaign=zero-landing"
            }
            sx={{ display: "inline-flex" }}
          >
            {logo}
          </Box>
          <Box
            sx={{
              maxWidth: "95vw",
              borderRadius: "12px",
              boxShadow: `0px 16px 80px 0px rgba(111, 67, 231, 0.16)`,
            }}
          >
            {children}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <Stack
        sx={{
          p: "10vh",
        }}
        direction="row"
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderWidth: "1px" }}
          />
        }
      >
        <Stack divider={<Divider sx={{ borderWidth: "1px" }} />}>
          <Box sx={{ px: 5, pb: "2vh" }}>
            <Stack>
              <Stack direction="row" spacing={10} alignItems={"center"}>
                <Box
                  component={Link}
                  href={
                    "https://www.pomerium.com/?utm_source=zero&utm_medium=landing&utm_campaign=zero-landing"
                  }
                >
                  {logo}
                </Box>
                <Link href="https://www.pomerium.com/customer-stories?utm_source=zero&utm_medium=landing&utm_campaign=zero-landing">
                  Customer Stories
                </Link>
                <Link href="https://www.pomerium.com/comparisons?utm_source=zero&utm_medium=landing&utm_campaign=zero-landing">
                  Comparisons
                </Link>
              </Stack>
              <List sx={{ mt: 2 }}>
                <ListItem sx={{ ml: 0, pl: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#F1ECFD" }}>
                      <EasilyManage />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Easily manage your deployments and users"
                    secondary="Manage users, routes, and policies with ease in Zero's slick web-based UI."
                  />
                </ListItem>
                <ListItem sx={{ ml: 0, pl: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#F1ECFD" }}>
                      <IntuitiveApi />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Intuitive API"
                    secondary="Add access control the same way you ship code."
                  />
                </ListItem>
                <ListItem sx={{ ml: 0, pl: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#F1ECFD" }}>
                      <PrivateData />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Your data stays private"
                    secondary="You host the proxy, so we can't see anything you don't want us to."
                  />
                </ListItem>
                <ListItem sx={{ ml: 0, pl: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: "#F1ECFD" }}>
                      <Users />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Let your users take the direct flight"
                    secondary="No extra hops means every user's request is faster and also cheaper because
                    there are no ingress or egress costs"
                  />
                </ListItem>
              </List>
            </Stack>
          </Box>
          <Box sx={{ px: 5, pt: "2vh", pb: "10vh" }}>
            <Carousel>
              <CarouselSlide>
                <List>
                  <ListItemText>
                    “We can confidently tell clients and auditors: `we only give
                    people access to the things they need.` How we prove that is
                    we can pull up the Pomerium configuration and say... `this
                    team has access to these resources.`”
                  </ListItemText>
                  <ListItem sx={{ ml: 0, pl: 0 }}>
                    <ListItemAvatar>
                      <Avatar src="/zach.jpeg" />
                    </ListItemAvatar>
                    <ListItemText primary="Zach Dunn" secondary="CISO" />
                  </ListItem>
                </List>
              </CarouselSlide>
              <CarouselSlide>
                <List>
                  <ListItemText>
                    “Pomerium enables true zero trust security without getting
                    in the way of workflow or productivity. The end user
                    experience is simple and onboarding has never been easier.”
                  </ListItemText>
                  <ListItem sx={{ ml: 0, pl: 0 }}>
                    <ListItemAvatar>
                      <Avatar src="/bri.png" />
                    </ListItemAvatar>
                    <ListItemText
                      primary="Bri Hatch"
                      secondary="Sr. Director of IT Operations at ExtraHop"
                    />
                  </ListItem>
                </List>
              </CarouselSlide>
              <CarouselSlide>
                <List>
                  <ListItemText>
                    “With Pomerium integrated, we now have a good handle on how
                    to secure our internal apps and high-value assets if they
                    don't have their own authorization capabilities.”
                  </ListItemText>
                  <ListItem sx={{ ml: 0, pl: 0 }}>
                    <ListItemAvatar>
                      <Avatar src="/shreyas.jpeg" />
                    </ListItemAvatar>
                    <ListItemText
                      primary="Shreyas Karnik"
                      secondary="Software Engineering Lead"
                    />
                  </ListItem>
                </List>
              </CarouselSlide>
              <CarouselSlide>
                <List>
                  <ListItemText>
                    “We can say to the developer teams: Yeah, you don't need to
                    worry about this, we got you covered, it's fine. We're
                    actually saving them work - something that's unheard of for
                    a security team.”
                  </ListItemText>
                  <ListItem sx={{ ml: 0, pl: 0 }}>
                    <ListItemAvatar>
                      <Avatar src="/lucas.png" />
                    </ListItemAvatar>
                    <ListItemText
                      primary="Lucas Cioffi"
                      secondary="Head of Information Security and IT"
                    />
                  </ListItem>
                </List>
              </CarouselSlide>
            </Carousel>
          </Box>
        </Stack>
        <Box
          sx={{
            pl: "5vh",
            pr: "10vh",
          }}
        >
          <Box
            sx={{
              borderRadius: "12px",
              boxShadow: `0px 16px 80px 0px rgba(111, 67, 231, 0.16)`,
            }}
          >
            {children}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default LoginShell;
